import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MusicianCard } from '@/components/MusicianCard';
import { 
  MusicNote, House, Heart, MagnifyingGlass, SignOut, Gear, Hash, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { icon: House, label: 'Scopri', path: '/organizer' },
    { icon: Heart, label: 'Preferiti', path: '/organizer/favorites' },
    { icon: Gear, label: 'Profilo', path: '/organizer/profile' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0A] border-r border-white/10 flex flex-col z-30">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-[#FF0055] flex items-center justify-center">
            <MusicNote size={24} weight="fill" className="text-white" />
          </div>
          <span className="font-bold text-xl">BeatPromoter</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActive ? 'bg-[#FF0055]/10 text-[#FF0055]' : 'text-[#A1A1AA]'
              }`}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
            >
              <item.icon size={20} weight={isActive ? 'fill' : 'regular'} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User hashtags */}
      {user?.genre_hashtags && user.genre_hashtags.length > 0 && (
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-xs text-[#666] uppercase tracking-wider mb-2">I tuoi generi</p>
          <div className="flex flex-wrap gap-1">
            {user.genre_hashtags.map(hashtag => (
              <span key={hashtag} className="text-xs px-2 py-1 bg-[#FF0055]/20 text-[#FF0055] rounded-full">
                {hashtag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* User */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
            <MusicNote size={18} weight="fill" className="text-[#FF0055]" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-[#A1A1AA]">Organizzatore</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-red-400 transition-colors text-sm"
          data-testid="sidebar-logout"
        >
          <SignOut size={18} />
          Esci
        </button>
      </div>
    </aside>
  );
};

// Discover Page
const DiscoverMusicians = () => {
  const { user } = useAuth();
  const [musicians, setMusicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState([]);
  const [genreCategories, setGenreCategories] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchMusicians();
  }, [selectedHashtag, search]);

  const fetchInitialData = async () => {
    try {
      const [genresRes, categoriesRes, favoritesRes] = await Promise.all([
        axios.get(`${API}/genres`),
        axios.get(`${API}/genre-categories`),
        axios.get(`${API}/favorites`),
      ]);
      setGenres(genresRes.data);
      setGenreCategories(categoriesRes.data);
      setFavorites(favoritesRes.data.map(f => f.id));
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };

  const fetchMusicians = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedHashtag && selectedHashtag !== 'all') {
        params.append('hashtag', selectedHashtag);
      }
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/discover/musicians?${params}`);
      setMusicians(response.data);
    } catch (error) {
      console.error('Failed to fetch musicians:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (musician) => {
    try {
      if (favorites.includes(musician.id)) {
        const favRes = await axios.get(`${API}/favorites`);
        const fav = favRes.data.find(f => f.id === musician.id);
        if (fav?.favorite_id) {
          await axios.delete(`${API}/favorites/${fav.favorite_id}`);
          setFavorites(prev => prev.filter(id => id !== musician.id));
          toast.success('Rimosso dai preferiti');
        }
      } else {
        await axios.post(`${API}/favorites`, { musician_id: musician.id });
        setFavorites(prev => [...prev, musician.id]);
        toast.success('Aggiunto ai preferiti');
      }
    } catch (error) {
      toast.error('Errore durante l\'operazione');
    }
  };

  // User's preferred hashtags first
  const userHashtags = user?.genre_hashtags || [];
  const otherHashtags = genreCategories.filter(c => !userHashtags.includes(`#${c}`));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Scopri nuovi talenti</h1>
        <p className="text-[#A1A1AA]">Esplora artisti e band emergenti filtrati per genere</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca artisti..."
          className="pl-10 bg-[#111] border-white/10"
          data-testid="search-input"
        />
      </div>

      {/* Hashtag Filters */}
      <div className="space-y-3">
        <p className="text-sm text-[#A1A1AA]">Filtra per genere:</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedHashtag('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
              selectedHashtag === 'all'
                ? 'bg-[#FF0055] text-white'
                : 'bg-[#111] text-[#A1A1AA] border border-white/10 hover:border-[#FF0055]'
            }`}
          >
            Tutti
          </button>
          
          {/* User's preferred hashtags */}
          {userHashtags.length > 0 && (
            <>
              {userHashtags.map(hashtag => {
                const category = hashtag.replace('#', '');
                return (
                  <button
                    key={hashtag}
                    onClick={() => setSelectedHashtag(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                      selectedHashtag === category
                        ? 'bg-[#FF0055] text-white'
                        : 'bg-[#FF0055]/20 text-[#FF0055] border border-[#FF0055]/30 hover:bg-[#FF0055]/30'
                    }`}
                    data-testid={`filter-${category}`}
                  >
                    <Hash size={14} />
                    {category}
                  </button>
                );
              })}
              <div className="w-px h-8 bg-white/10 mx-2" />
            </>
          )}
          
          {/* Other hashtags */}
          {otherHashtags.map(category => (
            <button
              key={category}
              onClick={() => setSelectedHashtag(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                selectedHashtag === category
                  ? 'bg-[#FF0055] text-white'
                  : 'bg-[#111] text-[#A1A1AA] border border-white/10 hover:border-[#FF0055]'
              }`}
              data-testid={`filter-${category}`}
            >
              <Hash size={14} />
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Musicians Grid */}
      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#FF0055] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : musicians.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {musicians.map((musician, index) => (
            <MusicianCard
              key={musician.id}
              musician={musician}
              index={index}
              isFavorite={favorites.includes(musician.id)}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-24">
          <MusicNote size={64} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">Nessun artista trovato per questo genere</p>
          {selectedHashtag !== 'all' && (
            <button
              onClick={() => setSelectedHashtag('all')}
              className="text-[#FF0055] hover:underline mt-2"
            >
              Mostra tutti gli artisti
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Favorites Page
const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (musician) => {
    try {
      await axios.delete(`${API}/favorites/${musician.favorite_id}`);
      setFavorites(prev => prev.filter(f => f.id !== musician.id));
      toast.success('Rimosso dai preferiti');
    } catch (error) {
      toast.error('Errore durante la rimozione');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">I tuoi preferiti</h1>
        <p className="text-[#A1A1AA]">Artisti che hai salvato</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#FF0055] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : favorites.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {favorites.map((musician, index) => (
            <MusicianCard
              key={musician.id}
              musician={musician}
              index={index}
              isFavorite={true}
              onToggleFavorite={removeFavorite}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-24 card rounded-xl">
          <Heart size={64} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#A1A1AA] mb-4">Nessun artista nei preferiti</p>
          <Link to="/organizer">
            <Button className="bg-[#FF0055] text-white hover:bg-[#FF3377]">
              Scopri artisti
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// Profile Page with Hashtag Management
const OrganizerProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [genreCategories, setGenreCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    location: user?.location || '',
    bio: user?.bio || '',
    website: user?.website || '',
    genre_hashtags: user?.genre_hashtags || [],
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/genre-categories`);
      setGenreCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const toggleHashtag = (category) => {
    setFormData(prev => {
      const current = prev.genre_hashtags || [];
      const hashtag = `#${category}`;
      if (current.includes(hashtag)) {
        return { ...prev, genre_hashtags: current.filter(h => h !== hashtag) };
      } else if (current.length < 5) {
        return { ...prev, genre_hashtags: [...current, hashtag] };
      }
      toast.error('Massimo 5 hashtag selezionabili');
      return prev;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUser(formData);
      toast.success('Profilo aggiornato!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Profilo</h1>
        <p className="text-[#A1A1AA]">Modifica le informazioni del tuo profilo e i generi che cerchi</p>
      </div>

      <form onSubmit={handleSave} className="card rounded-xl p-6 max-w-2xl space-y-6">
        <div className="space-y-2">
          <Label className="text-white">Nome locale / Azienda</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-[#0A0A0A] border-white/10"
            data-testid="profile-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white">Città</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="Es. Roma"
            className="bg-[#0A0A0A] border-white/10"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white">Sito web</Label>
          <Input
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://..."
            className="bg-[#0A0A0A] border-white/10"
          />
        </div>

        {/* Hashtag Management */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-white">Generi che cerchi (max 5)</Label>
            <span className="text-xs text-[#A1A1AA]">{formData.genre_hashtags.length}/5</span>
          </div>
          <p className="text-xs text-[#666]">
            Seleziona le macro-categorie musicali che ti interessano. Gli artisti verranno filtrati in base a queste preferenze.
          </p>
          
          {/* Selected hashtags */}
          {formData.genre_hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-[#FF0055]/30">
              {formData.genre_hashtags.map(hashtag => (
                <span
                  key={hashtag}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF0055] text-white text-sm rounded-full font-medium"
                >
                  {hashtag}
                  <button type="button" onClick={() => toggleHashtag(hashtag.replace('#', ''))}>
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* All hashtags */}
          <div className="flex flex-wrap gap-2">
            {genreCategories.map(category => {
              const hashtag = `#${category}`;
              const isSelected = formData.genre_hashtags.includes(hashtag);
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => toggleHashtag(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                    isSelected
                      ? 'bg-[#FF0055] text-white'
                      : 'bg-[#111] text-[#A1A1AA] border border-white/10 hover:border-[#FF0055]'
                  }`}
                  data-testid={`profile-hashtag-${category}`}
                >
                  <Hash size={14} />
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="bg-[#FF0055] text-white hover:bg-[#FF3377]"
          data-testid="save-profile-btn"
        >
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </Button>
      </form>
    </div>
  );
};

// Main Organizer Dashboard
export default function OrganizerDashboard() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar />
      <main className="ml-64 p-8 pb-32" data-testid="organizer-dashboard">
        <Routes>
          <Route index element={<DiscoverMusicians />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="profile" element={<OrganizerProfilePage />} />
        </Routes>
      </main>
    </div>
  );
}
